import { useHasAccess } from 'lib-shared-access-control';
import { useCallback, useMemo } from 'react';
import { useMatch } from 'react-router-dom';

import { appActions } from 'app/app.actions';
import { canClaimConversation } from 'assets/components/conversation/utils/canClaimConversation';
import { canTakeOverConversation } from 'assets/components/conversation/utils/canTakeOverConversation';
import { canConversationTransferToAgent } from 'assets/components/conversation/utils/conversationTransfer';
import { getLatestFacebookConversation } from 'components/conversation/selectors';
import { actions as personalActions } from 'components/settings/actions/personal';
// eslint-disable-next-line no-restricted-imports -- Need legacy composeEmailActions for command launcher compose email functionality
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
import { CommandLauncherAction, useCommandLauncher } from 'utils/commandLauncher';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useServerStorage } from 'utils/useServerStorage';

/** Returns conversation-specific actions for the command launcher. */
export function useConversationActions(): readonly CommandLauncherAction[] {
  const dispatch = useAppDispatch();
  const { createAction } = useCommandLauncher();
  const storage = useServerStorage('user');
  const { hasCopilot } = useCopilotTrial();

  // Get current conversation for context-aware actions
  const metaConversation = useAppSelector(
    (state) => state.conversation.metaConversation,
  );

  // Get conversation offers for accept/reject actions
  const conversationOffers = useAppSelector(
    (state) => state.offers.conversationOffers,
  );

  // Get telephony state for hold/mute actions
  const telephony = useAppSelector((state) => state.telephony);

  // Feature flags
  const {
    conversationEditSubject,
    conversationResolutionState,
    linkMergeConversations,
  } = useFeatureFlag([
    'conversation-edit-subject',
    'conversation-resolution-state',
    'link-merge-conversations',
  ]);

  // Check if we're on a conversation page and get the csid
  const conversationRouteMatch = useMatch('/conversation/:csid/*');
  const isOnConversationPage = conversationRouteMatch !== null;
  const currentCsid = conversationRouteMatch?.params.csid
    ? parseInt(conversationRouteMatch.params.csid, 10)
    : null;

  // Access control
  const hasClaimAccess = useHasAccess('conversation.claim');
  const hasCustomAttributeAccess = useHasAccess('contact.custom_attribute');
  const hasReplyAccess = useHasAccess('conversation.reply');
  const hasLogAccess = useHasAccess('conversation.log');

  // Get latest Facebook conversation for claim check
  const latestFacebookCsid = useAppSelector(getLatestFacebookConversation);

  // Get sidebar state
  const isCustomCardMenuOpen = useAppSelector(
    (state) => state.settings.personal.isCustomCardMenuOpen,
  );

  // Copy to clipboard helper
  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  return useMemo(() => {
    const actions: CommandLauncherAction[] = [];

    // Still add offer actions even when not on conversation page
    if (conversationOffers?.length > 0) {
      actions.push(
        createAction({
          label: 'Offer accept',
          aliases: ['accept', 'take offer', 'claim offer', 'accept offer'],
          icon: 'check-circle',
          section: 'Conversation',
          commandName: 'conversation:acceptOffer',
          performFn: () => {
            pubSub.publish('command:conversation:acceptOffer');
          },
        }),
        createAction({
          label: 'Offer reject',
          aliases: ['reject', 'decline offer', 'skip offer', 'reject offer'],
          icon: 'ban',
          section: 'Conversation',
          commandName: 'conversation:rejectOffer',
          performFn: () => {
            pubSub.publish('command:conversation:rejectOffer');
          },
        }),
      );
    }

    // Return early if not on conversation page or no valid conversation loaded
    if (!isOnConversationPage || !metaConversation?.conversation?.id) {
      return actions;
    }

    const { requester, isAssignedToMe, conversation } = metaConversation;

    // Core conversation actions (no permission required)
    actions.push(
      createAction({
        label: 'Copy conversation link',
        icon: 'link',
        section: 'Conversation',
        performFn: () => copyToClipboard(window.location.href),
      }),
      createAction({
        label: 'Toggle right sidebar',
        aliases: ['right menu', 'right sidebar', 'info panel', 'customer info'],
        icon: 'user',
        section: 'Conversation',
        commandName: 'conversation:toggleRightSidebar',
        performFn: () => {
          dispatch(
            personalActions.personalCustomCardMenu({
              key: 'isCustomCardMenuOpen',
              value: !isCustomCardMenuOpen,
            }),
          );
        },
      }),
    );

    // Toggle activity log - requires log permission
    if (hasLogAccess) {
      actions.push(
        createAction({
          label: 'Toggle activity log',
          aliases: ['history', 'events', 'log'],
          icon: 'activity',
          section: 'Conversation',
          commandName: 'conversation:activityLog',
          performFn: () => {
            pubSub.publish('command:conversation:activityLog');
          },
        }),
      );
    }

    // Copy contact information - only if requester has the field
    if (requester?.name) {
      actions.push(
        createAction({
          label: 'Copy contact name',
          aliases: ['copy name', 'requester name'],
          icon: 'user',
          section: 'Conversation',
          performFn: () => copyToClipboard(requester!.name!),
        }),
      );
    }

    if (requester?.email) {
      actions.push(
        createAction({
          label: 'Copy contact email',
          aliases: ['copy email', 'requester email', 'mail address'],
          icon: 'mail',
          section: 'Conversation',
          performFn: () => copyToClipboard(requester!.email!),
        }),
      );
    }

    if (requester?.phone_number) {
      actions.push(
        createAction({
          label: 'Copy contact phone',
          aliases: ['copy phone', 'requester phone', 'telephone', 'number'],
          icon: 'phone',
          section: 'Conversation',
          performFn: () => copyToClipboard(requester!.phone_number!),
        }),
      );
    }

    // Edit Contact Attributes - only if has permission and requester exists
    if (hasCustomAttributeAccess && requester) {
      actions.push(
        createAction({
          label: 'Edit contact attributes',
          aliases: ['custom attributes', 'custom fields', 'contact fields'],
          icon: 'input',
          section: 'Conversation',
          performFn: () => {
            dispatch(
              appActions.showModal({
                modal: {
                  type: 'contact:addContactAttributes',
                  arg: { user: requester! },
                },
              }),
            );
          },
        }),
      );
    }

    // Add tag - no specific permission required
    actions.push(
      createAction({
        label: 'Add tag',
        aliases: ['tag', 'label', 'categorize', 'add tag'],
        icon: 'tag',
        section: 'Conversation',
        commandName: 'conversation:tagsSelector',
        performFn: () => {
          pubSub.publish('command:conversation:tagsSelector');
        },
      }),
    );

    // Actions requiring reply permission
    if (hasReplyAccess) {
      actions.push(
        createAction({
          label: 'Transfer to queue',
          aliases: ['move', 'reassign', 'queue'],
          icon: 'arrow-right',
          section: 'Conversation',
          performFn: () => {
            if (currentCsid) {
              dispatch(
                appActions.showModal({
                  modal: {
                    type: 'app:conversationQueueTransfer',
                    arg: { csid: currentCsid },
                  },
                }),
              );
            }
          },
        }),
        createAction({
          label: 'Follow-up',
          aliases: ['snooze', 'remind', 'later', 'pending', 'set follow-up'],
          icon: 'clock',
          section: 'Conversation',
          commandName: 'conversation:followUp',
          performFn: () => {
            pubSub.publish('command:conversation:followUp');
          },
        }),
        createAction({
          label: 'Toggle reply/note',
          aliases: [
            'reply',
            'respond',
            'answer',
            'message',
            'note',
            'internal',
            'private note',
            'team note',
            'comment',
            'switch editor',
          ],
          icon: 'exchange',
          section: 'Conversation',
          commandName: 'conversation:internalNote',
          performFn: () => {
            pubSub.publish('command:conversation:internalNote');
          },
        }),
      );
    }

    // Transfer to Agent - only if allowed for this conversation
    if (canConversationTransferToAgent(metaConversation)) {
      actions.push(
        createAction({
          label: 'Transfer to agent',
          aliases: ['assign', 'handover', 'reassign agent'],
          icon: 'exchange',
          section: 'Conversation',
          commandName: 'conversation:transferToAgent',
          performFn: () => {
            pubSub.publish('command:conversation:transferToAgent');
          },
        }),
      );
    }

    // Close Conversation - only if assigned, not already closed, and has permission
    if (isAssignedToMe && !conversation.is_closed && hasReplyAccess) {
      actions.push(
        createAction({
          label: 'Close conversation',
          aliases: ['end', 'resolve', 'done', 'finish', 'close'],
          icon: 'check-circle',
          section: 'Conversation',
          commandName: 'conversation:close',
          performFn: () => {
            pubSub.publish('command:conversation:close');
          },
        }),
      );
    }

    // Resolve/Unresolve Conversation - only for email/contactform channels
    const supportsResolution =
      conversation.initial_channel === 'email' ||
      conversation.initial_channel === 'contactform';

    if (conversationResolutionState && supportsResolution && hasReplyAccess) {
      const isResolved = !!conversation.resolution?.resolvedAt;
      actions.push(
        createAction({
          label: isResolved ? 'Unresolve conversation' : 'Resolve conversation',
          aliases: ['resolve', 'unresolve', 'resolution', 'mark resolved'],
          icon: 'check-circle',
          section: 'Conversation',
          performFn: () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pubSub.publish('command:conversation:resolve' as any);
          },
        }),
      );
    }

    // Claim/Take Over Conversation
    const canClaim = canClaimConversation(
      conversation,
      isAssignedToMe,
      latestFacebookCsid ?? 0,
    );
    const canTakeOver = canTakeOverConversation(conversation, isAssignedToMe);

    if (hasClaimAccess && (canClaim || canTakeOver)) {
      actions.push(
        createAction({
          label: canTakeOver ? 'Take over conversation' : 'Claim conversation',
          aliases: ['grab', 'take', 'mine', 'assign to me', 'claim'],
          icon: 'user-plus',
          section: 'Conversation',
          commandName: 'conversation:claim',
          performFn: () => {
            pubSub.publish('command:conversation:claim');
          },
        }),
      );
    }

    // Edit Subject - requires feature flag
    if (conversationEditSubject) {
      actions.push(
        createAction({
          label: 'Edit subject',
          aliases: ['subject', 'title', 'rename', 'change subject'],
          icon: 'pen',
          section: 'Conversation',
          commandName: 'conversation:editSubject',
          performFn: () => {
            pubSub.publish('command:conversation:editSubject');
          },
        }),
      );
    }

    // Create Side Conversation
    actions.push(
      createAction({
        label: 'Create side conversation',
        aliases: ['side', 'linked conversation', 'sub conversation', 'thread'],
        icon: 'mail',
        section: 'Conversation',
        performFn: () => {
          if (currentCsid) {
            const initialValues = {
              parentConversation: currentCsid,
              linkConversationType: 'side',
            };
            dispatch(
              composeEmailActions.composeEmailOpenModal({ initialValues }),
            );
          }
        },
      }),
    );

    // Send Transcript - only for email, widget chat, and contact form channels
    const channel = conversation.initial_channel;
    const supportsTranscript = ['email', 'widgetchat', 'contactform'].includes(
      channel ?? '',
    );
    if (supportsTranscript) {
      actions.push(
        createAction({
          label: 'Send transcript',
          aliases: ['forward', 'email forward', 'transcript', 'export'],
          icon: 'mail',
          section: 'Conversation',
          performFn: () => {
            if (currentCsid) {
              const initialValues = {
                parentConversation: currentCsid,
                linkConversationType: 'email_forward',
              };
              dispatch(
                composeEmailActions.composeEmailOpenModal({ initialValues }),
              );
            }
          },
        }),
      );
    }

    // Link Conversations - requires feature flag
    if (linkMergeConversations) {
      actions.push(
        createAction({
          label: 'Link conversations',
          aliases: ['merge', 'combine', 'link', 'related'],
          icon: 'link',
          section: 'Conversation',
          performFn: () => {
            storage.set({
              conversation: (draft) => {
                draft.isTimelineExpanded = true;
              },
            });
          },
        }),
      );
    }

    // Translate Conversation - requires Copilot
    if (hasCopilot) {
      actions.push(
        createAction({
          label: 'Translate conversation',
          aliases: ['translate', 'language', 'translation', 'ai translate'],
          icon: 'translate',
          section: 'Conversation',
          commandName: 'conversation:translate',
          performFn: () => {
            pubSub.publish('command:conversation:translate');
          },
        }),
      );

      // Toggle Summary - only if summary can be generated
      if (conversation.summary?.can_be_generated) {
        actions.push(
          createAction({
            label: 'Toggle summary',
            aliases: ['show summary', 'hide summary', 'summary', 'ai summary'],
            icon: 'list-ul',
            section: 'Conversation',
            commandName: 'conversation:summary',
            performFn: () => {
              pubSub.publish('command:conversation:summary');
            },
          }),
        );
      }
    }

    // Note: Change CSAT is intentionally not included here because it requires
    // additional API-based eligibility checks (csat_eligible, cancellable,
    // user_excluded_from_csat) that are not available in the command launcher context.
    // The CSAT action is available through the conversation toolbar UI.

    // Hold/Mute Call - only on active phone calls
    const isTelephonyChannel =
      conversation.initial_channel === 'pstnphone' ||
      conversation.initial_channel === 'callback';
    const isActivePhoneCall =
      !conversation.is_closed &&
      isAssignedToMe &&
      isTelephonyChannel &&
      telephony.currentCall !== null;

    if (isActivePhoneCall) {
      actions.push(
        createAction({
          label: telephony.isOnHold ? 'Resume call' : 'Hold Call',
          aliases: ['hold', 'pause call', 'resume'],
          icon: 'pause',
          section: 'Conversation',
          commandName: 'conversation:phoneToggleHold',
          performFn: () => {
            pubSub.publish('command:conversation:phoneToggleHold');
          },
        }),
        createAction({
          label: telephony.isOnMute ? 'Unmute call' : 'Mute call',
          aliases: ['mute', 'unmute', 'silence'],
          icon: 'phone',
          section: 'Conversation',
          commandName: 'conversation:phoneToggleMute',
          performFn: () => {
            pubSub.publish('command:conversation:phoneToggleMute');
          },
        }),
      );
    }

    return actions;
  }, [
    conversationEditSubject,
    conversationOffers,
    conversationResolutionState,
    copyToClipboard,
    createAction,
    currentCsid,
    dispatch,
    hasCopilot,
    hasClaimAccess,
    hasCustomAttributeAccess,
    hasLogAccess,
    hasReplyAccess,
    isCustomCardMenuOpen,
    isOnConversationPage,
    latestFacebookCsid,
    linkMergeConversations,
    metaConversation,
    storage,
    telephony,
  ]);
}
